import posthogJs from "posthog-js";

import { env } from "~/env";
import { logger } from "~/lib/logger";

// Re-export posthog instance for advanced usage
export { default as posthog } from "posthog-js";

const isDevelopment = import.meta.env.DEV;

/**
 * Initialize PostHog with full configuration
 * Call this once at app startup
 */
export function initPostHog() {
  if (!env.PUBLIC_POSTHOG_KEY) {
    if (!isDevelopment) {
      logger.warn("posthog_missing_api_key", {
        message: "No API key provided, skipping initialization",
      });
    }
    return;
  }

  // Skip PostHog in development to avoid 401 errors from dev/test API keys
  if (isDevelopment) {
    return;
  }

  posthogJs.init(env.PUBLIC_POSTHOG_KEY, {
    api_host: env.PUBLIC_POSTHOG_HOST,

    // Use latest defaults
    defaults: "2025-05-24",

    // Capture everything for product analytics
    autocapture: true,
    capture_pageview: true,
    capture_pageleave: true,

    // Session recording - PostHog's strength for product analytics
    disable_session_recording: false,
    session_recording: {
      // Mask sensitive data in recordings
      maskAllInputs: true,
      maskTextSelector: "[data-ph-mask]",
    },

    // Performance and heatmaps
    enable_heatmaps: true,
    capture_performance: true,

    // Feature flags - fetch on load for immediate availability
    bootstrap: {},
    advanced_disable_feature_flags: false,

    // Privacy settings
    respect_dnt: false, // We handle consent separately
    secure_cookie: !isDevelopment,

    // Cross-domain tracking for subdomains
    cross_subdomain_cookie: true,

    // Debug mode in development
    debug: isDevelopment,

    // Keep PostHog focused on product analytics and replay.
    capture_exceptions: false,

    // Loaded callback
    loaded: (ph) => {
      if (isDevelopment) {
        // Expose for debugging
        Object.assign(window, { posthog: ph });
      }
    },
  });
}

/**
 * Identify user with PostHog - call after authentication
 */
export function identifyUser(user: {
  id: string;
  email?: string;
  name?: string;
  createdAt?: string;
  organizationId?: string;
  organizationName?: string;
  role?: string;
}) {
  posthogJs.identify(user.id, {
    email: user.email,
    name: user.name,
    created_at: user.createdAt,
    organization_id: user.organizationId,
    organization_name: user.organizationName,
    role: user.role,
  });

  // Set up group analytics for organization/restaurant
  if (user.organizationId) {
    posthogJs.group("organization", user.organizationId, {
      name: user.organizationName,
    });
  }
}

/**
 * Set location context for group analytics
 */
export function setLocationContext(location: {
  id: string;
  name: string;
  slug?: string;
  plan?: string;
  createdAt?: string;
}) {
  posthogJs.group("location", location.id, {
    name: location.name,
    slug: location.slug,
    plan: location.plan,
    created_at: location.createdAt,
  });
}

/**
 * Reset user identity (call on logout)
 */
export function resetUser() {
  posthogJs.reset();
}

/**
 * Track a custom event
 */
export function trackEvent(
  eventName: string,
  properties?: Record<string, unknown>
) {
  posthogJs.capture(eventName, properties);
}

/**
 * Pre-defined product events for consistency
 */
export const ProductEvents = {
  // Onboarding
  onboardingStarted: () => trackEvent("onboarding_started"),
  onboardingStepCompleted: (step: string, stepNumber: number) =>
    trackEvent("onboarding_step_completed", { step, step_number: stepNumber }),
  onboardingCompleted: (totalSteps: number) =>
    trackEvent("onboarding_completed", { total_steps: totalSteps }),

  // Menu management
  menuCreated: (menuId: string) =>
    trackEvent("menu_created", { menu_id: menuId }),
  menuUpdated: (menuId: string) =>
    trackEvent("menu_updated", { menu_id: menuId }),
  menuDeleted: (menuId: string) =>
    trackEvent("menu_deleted", { menu_id: menuId }),
  menuItemCreated: (itemId: string, categoryId: string) =>
    trackEvent("menu_item_created", {
      item_id: itemId,
      category_id: categoryId,
    }),
  menuItemUpdated: (itemId: string) =>
    trackEvent("menu_item_updated", { item_id: itemId }),
  menuItemDeleted: (itemId: string) =>
    trackEvent("menu_item_deleted", { item_id: itemId }),
  categoryCreated: (categoryId: string) =>
    trackEvent("category_created", { category_id: categoryId }),
  categoryDeleted: (categoryId: string) =>
    trackEvent("category_deleted", { category_id: categoryId }),

  // Orders
  orderCreated: (orderId: string, total: number, itemCount: number) =>
    trackEvent("order_created", {
      order_id: orderId,
      total,
      item_count: itemCount,
    }),
  orderCompleted: (orderId: string, total: number) =>
    trackEvent("order_completed", { order_id: orderId, total }),
  orderCancelled: (orderId: string, reason?: string) =>
    trackEvent("order_cancelled", { order_id: orderId, reason }),

  // Payments
  paymentInitiated: (orderId: string, method: string, amount: number) =>
    trackEvent("payment_initiated", { order_id: orderId, method, amount }),
  paymentCompleted: (orderId: string, method: string, amount: number) =>
    trackEvent("payment_completed", { order_id: orderId, method, amount }),
  paymentFailed: (orderId: string, method: string, error: string) =>
    trackEvent("payment_failed", { order_id: orderId, method, error }),
  paymentMethodUpdated: () => trackEvent("payment_method_updated"),

  // Settings
  settingsUpdated: (section: string) =>
    trackEvent("settings_updated", { section }),

  // Location management
  locationCreated: (locationId: string) =>
    trackEvent("location_created", { location_id: locationId }),
  locationUpdated: (locationId: string, section: string) =>
    trackEvent("location_updated", { location_id: locationId, section }),
  locationDeleted: (locationId: string) =>
    trackEvent("location_deleted", { location_id: locationId }),

  // Employee management
  employeeCreated: (employeeId: string) =>
    trackEvent("employee_created", { employee_id: employeeId }),
  employeeUpdated: (employeeId: string) =>
    trackEvent("employee_updated", { employee_id: employeeId }),
  employeeDeleted: (employeeId: string) =>
    trackEvent("employee_deleted", { employee_id: employeeId }),

  // Shift management
  shiftCreated: (shiftId: string) =>
    trackEvent("shift_created", { shift_id: shiftId }),
  shiftUpdated: (shiftId: string) =>
    trackEvent("shift_updated", { shift_id: shiftId }),
  shiftDeleted: (shiftId: string) =>
    trackEvent("shift_deleted", { shift_id: shiftId }),

  // Category management (extended)
  categoryUpdated: (categoryId: string) =>
    trackEvent("category_updated", { category_id: categoryId }),

  // Modifier group management
  modifierGroupCreated: (modifierGroupId: string) =>
    trackEvent("modifier_group_created", {
      modifier_group_id: modifierGroupId,
    }),
  modifierGroupUpdated: (modifierGroupId: string) =>
    trackEvent("modifier_group_updated", {
      modifier_group_id: modifierGroupId,
    }),
  modifierGroupDeleted: (modifierGroupId: string) =>
    trackEvent("modifier_group_deleted", {
      modifier_group_id: modifierGroupId,
    }),
  modifierGroupDuplicated: (sourceId: string, newId: string) =>
    trackEvent("modifier_group_duplicated", {
      source_id: sourceId,
      new_id: newId,
    }),

  // Discount management
  discountCreated: (discountId: string, locationId: string) =>
    trackEvent("discount_created", {
      discount_id: discountId,
      location_id: locationId,
    }),
  discountUpdated: (discountId: string) =>
    trackEvent("discount_updated", { discount_id: discountId }),
  discountDeleted: (discountId: string) =>
    trackEvent("discount_deleted", { discount_id: discountId }),

  // Device management
  deviceUpdated: (deviceId: string) =>
    trackEvent("device_updated", { device_id: deviceId }),
  deviceDeleted: (deviceId: string) =>
    trackEvent("device_deleted", { device_id: deviceId }),
  terminalRemoved: (deviceId: string) =>
    trackEvent("terminal_removed", { device_id: deviceId }),
  terminalSetup: (locationId: string) =>
    trackEvent("terminal_setup", { location_id: locationId }),

  // Tax category management
  taxCategoryCreated: (categoryId: string) =>
    trackEvent("tax_category_created", { category_id: categoryId }),
  taxCategoryUpdated: (categoryId: string) =>
    trackEvent("tax_category_updated", { category_id: categoryId }),
  taxCategoryDeleted: (categoryId: string) =>
    trackEvent("tax_category_deleted", { category_id: categoryId }),

  // Sales category management
  salesCategoryCreated: (categoryId: string) =>
    trackEvent("sales_category_created", { category_id: categoryId }),
  salesCategoryUpdated: (categoryId: string) =>
    trackEvent("sales_category_updated", { category_id: categoryId }),
  salesCategoryDeleted: (categoryId: string) =>
    trackEvent("sales_category_deleted", { category_id: categoryId }),

  // Station management
  stationCreated: (stationId: string, locationId: string) =>
    trackEvent("station_created", {
      station_id: stationId,
      location_id: locationId,
    }),
  stationUpdated: (stationId: string) =>
    trackEvent("station_updated", { station_id: stationId }),
  stationDeleted: (stationId: string) =>
    trackEvent("station_deleted", { station_id: stationId }),

  // Reader management
  readerUpdated: (readerId: string) =>
    trackEvent("reader_updated", { reader_id: readerId }),
  readerDeleted: (readerId: string) =>
    trackEvent("reader_deleted", { reader_id: readerId }),
  readerRegistered: (locationId: string) =>
    trackEvent("reader_registered", { location_id: locationId }),

  // Organization member management
  memberInvited: (email: string, role: string) =>
    trackEvent("member_invited", { email, role }),
  memberRemoved: (userId: string) =>
    trackEvent("member_removed", { user_id: userId }),
  memberRoleChanged: (userId: string, newRole: string) =>
    trackEvent("member_role_changed", { user_id: userId, new_role: newRole }),

  // Organization role management
  roleCreated: (roleId: string) =>
    trackEvent("role_created", { role_id: roleId }),
  roleUpdated: (roleId: string) =>
    trackEvent("role_updated", { role_id: roleId }),
  roleDeleted: (roleId: string) =>
    trackEvent("role_deleted", { role_id: roleId }),

  // Feature usage
  featureUsed: (featureName: string, metadata?: Record<string, unknown>) =>
    trackEvent("feature_used", { feature: featureName, ...metadata }),
};

/**
 * Feature flags helpers
 */
export const FeatureFlags = {
  /**
   * Check if a feature flag is enabled
   */
  isEnabled: (flagKey: string): boolean => {
    return posthogJs.isFeatureEnabled(flagKey) ?? false;
  },

  /**
   * Get feature flag value (for multivariate flags)
   */
  getValue: (flagKey: string): string | boolean | undefined => {
    const value = posthogJs.getFeatureFlag(flagKey);
    if (typeof value === "string" || typeof value === "boolean") {
      return value;
    }
    return undefined;
  },

  /**
   * Get feature flag payload (for JSON payloads)
   */
  getPayload: (flagKey: string): Record<string, unknown> | undefined => {
    const payload = posthogJs.getFeatureFlagPayload(flagKey);
    if (
      payload !== null &&
      payload !== undefined &&
      typeof payload === "object" &&
      !Array.isArray(payload)
    ) {
      return payload;
    }
    return undefined;
  },

  /**
   * Reload feature flags from server
   */
  reload: () => {
    posthogJs.reloadFeatureFlags();
  },

  /**
   * Register callback for flag changes
   */
  onUpdate: (callback: () => void) => {
    posthogJs.onFeatureFlags(callback);
  },
};

/**
 * A/B Testing / Experiments helpers
 */
export const Experiments = {
  /**
   * Get the variant for an experiment
   */
  getVariant: (experimentKey: string): string | undefined => {
    const variant = posthogJs.getFeatureFlag(experimentKey);
    return typeof variant === "string" ? variant : undefined;
  },

  /**
   * Track experiment exposure (called automatically by getFeatureFlag)
   * Use this for manual tracking if needed
   */
  trackExposure: (experimentKey: string, variant: string) => {
    trackEvent("$experiment_exposure", {
      $feature_flag: experimentKey,
      $feature_flag_response: variant,
    });
  },
};

/**
 * Surveys helpers
 */
export const Surveys = {
  /**
   * Get active surveys for current user
   */
  getActive: (callback: (surveys: unknown[]) => void) => {
    posthogJs.getActiveMatchingSurveys(callback);
  },

  /**
   * Capture survey shown event
   */
  shown: (surveyId: string) => {
    trackEvent("survey_shown", { survey_id: surveyId });
  },

  /**
   * Capture survey dismissed event
   */
  dismissed: (surveyId: string) => {
    trackEvent("survey_dismissed", { survey_id: surveyId });
  },

  /**
   * Capture survey response
   */
  responded: (surveyId: string, response: Record<string, unknown>) => {
    trackEvent("survey_responded", { survey_id: surveyId, ...response });
  },
};

/**
 * Page view tracking (manual, if needed beyond autocapture)
 */
export function trackPageView(
  pageName?: string,
  properties?: Record<string, unknown>
) {
  posthogJs.capture("$pageview", {
    $current_url: window.location.href,
    page_name: pageName,
    ...properties,
  });
}

/**
 * Set super properties (added to all events)
 */
export function setSuperProperties(properties: Record<string, unknown>) {
  posthogJs.register(properties);
}

/**
 * Set one-time properties (only sent once)
 */
export function setOnceProperties(properties: Record<string, unknown>) {
  posthogJs.register_once(properties);
}
